import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAnalytics } from 'core/analytics/hooks';
import {
  useCookieConsent,
  useDeepMemo,
  useDispatch,
  useIsomorphicLayoutEffect,
  usePostMessageToFunnelBuilderOnRouteChange,
  useRedirectToAskNebula,
  useServices,
} from 'core/common/hooks';
import { useObservability } from 'core/common/observability/context';
import {
  EmailFlowAttributes,
  EvaluatedContentAttributes,
  ScreenAttributes,
  ScreenType,
} from 'core/flow/entities';
import {
  prepareContentAttributes,
  useEmailFlow,
  useFlowAttributes,
  useFlowConditions,
  useFlowContent,
  useFlowContentAttributes,
  useFlowNavigation,
  useTrialChoiceSkip,
} from 'core/flow/hooks';
import { useFetchOffers } from 'core/offers/hooks';
import {
  Locales,
  UserInteractionFields,
  UserProfile,
  UserResponse,
  UserSettings,
  UserType,
} from 'core/user/entities';
import { useUser, useUserInteraction, useUserSettings } from 'core/user/hooks';
import { addReading, createOrder, validateEmailRequest } from 'core/user/store/actions';

import { useFunnelData } from './FunnelContext';

const transitScreenTypes = new Set<ScreenType>([ScreenType.SPLIT, ScreenType.NEB_SPLIT]);

export type QuizContextData = {
  getScreenAttributes: (id: string) => ScreenAttributes;
  userPreferences: UserProfile;
  completeQuiz: (email: string, productName: string) => Promise<{ isActiveSubscription: boolean }>;
  updateUserSettings: (data: Partial<UserSettings>) => void;
  updateUserEmailConsent: (emailConsent: boolean) => void;
  extraNext: (id: string) => void;
  next: (args: NextArgs) => Promise<string>;
  back: (id: string) => void;
  link: string;
  signIn: () => void;
  flowInitialScreenId: string;
  updateDynamicAttributes: (dataKey: string, pageId: string) => void;
  updateScreensPath: (screenId: string) => void;
  userResponses: Record<string, UserResponse>;
  updateUserData: (dataKey: string, data: UserResponse) => void;
  fetchOffers: () => void;
  updateFieldInteraction: (dataKey: UserInteractionFields) => void;
  flowContent: EvaluatedContentAttributes;
  emailFlowAttributes: EmailFlowAttributes['emailFlowAttributes'];
};

export const QuizContext = createContext<QuizContextData>(undefined!);

export type FlowProviderProps = {
  children?: ReactNode;
  variant?: 'default' | 'new';
};

export type NextArgs = {
  id: string;
  value?: string | string[];
};

export const FlowProvider = ({ children }: FlowProviderProps) => {
  const dispatch = useDispatch();

  const {
    link,
    flow,
    analyticsIds,
    attributes: funnelAttributes = {},
    offers: funnelOffers = { attributes: [] },
    project: funnelProject,
  } = useFunnelData();
  const projectAttributes = useMemo(
    () => funnelProject.attributes ?? {},
    [funnelProject.attributes],
  );

  const { transformContentToTemplate } = useFlowContent();

  const { getAttributesByCondition, getAttributesByFeature } = useFlowConditions();

  const { getScreenAttributes, updateDynamicAttributes } = useFlowAttributes({
    screens: flow.screens,
    flowInitialScreenId: flow.initialScreen,
    dynamicUserProfile: flow.attributes.dynamicUserProfile,
    getAttributesByFeature,
    getAttributesByCondition,
    transformContentToTemplate,
  });

  const preparedFlowAttributes = useMemo(
    () => prepareContentAttributes([flow.attributes, funnelAttributes, projectAttributes]),
    [flow.attributes, funnelAttributes, projectAttributes],
  );

  const evaluatedFlowAttributes = useFlowContentAttributes(preparedFlowAttributes);

  const getScreenAttributesForNavigation = useTrialChoiceSkip(getScreenAttributes);

  const { goNext, goExtraNext, goBack } = useFlowNavigation({
    rootFunnelLink: link,
    flowInitialScreenId: flow.initialScreen,
    getScreenAttributes: getScreenAttributesForNavigation,
  });

  const evaluatedOffersAttributes = useMemo(
    () => (flow.offers.attributes.length !== 0 ? flow.offers.attributes : funnelOffers.attributes),
    [flow.offers.attributes, funnelOffers.attributes],
  );

  const { fetchOffers } = useFetchOffers({ offersAttributes: evaluatedOffersAttributes });

  const evaluatedEmailFlowAttributes = useMemo(() => {
    return flow.attributes.emailFlowAttributes ?? funnelAttributes.emailFlowAttributes;
  }, [flow.attributes.emailFlowAttributes, funnelAttributes.emailFlowAttributes]);

  const { getEmailFlow } = useEmailFlow();
  const { redirectToLogin } = useRedirectToAskNebula();
  const { updateEmailConsentRequest, updateUserSettingsRequest } = useUserSettings();
  const { userPreferences, userResponses, updateUserPreferences, updateUserResponses } = useUser();
  const { updateFieldInteraction } = useUserInteraction();
  const { subscriptionService, userService, funnelSettingsService, funnelStepService } =
    useServices();

  const completeQuiz = async (email: string, productName: string) => {
    const emailFlow = getEmailFlow(evaluatedEmailFlowAttributes);

    await dispatch(validateEmailRequest(email));

    updateUserPreferences({ email });

    // "await" is important here to ensure that the "access_token" is set before fetching offers
    await dispatch(
      createOrder({
        email,
        offerName: productName,
        analyticsIds,
        emailFlow,
        businessUnit: evaluatedFlowAttributes.businessUnit?.value ?? null,
      }),
    );

    // "await" is important here to ensure that the default offers are set before adding readings
    await fetchOffers();

    const isActiveSubscription = await subscriptionService.isActiveSubscription();

    if (isActiveSubscription) {
      await dispatch(addReading(evaluatedEmailFlowAttributes));
    }

    return { isActiveSubscription };
  };

  const { defaultUserProfile } = flow.attributes;

  useEffect(() => {
    if (!defaultUserProfile) {
      return;
    }

    updateUserPreferences(defaultUserProfile);
  }, [defaultUserProfile, updateUserPreferences]);

  useEffect(() => {
    if (userPreferences.userType !== UserType.LEGAL) return;

    fetchOffers();

    const emailFlow = getEmailFlow(evaluatedEmailFlowAttributes);

    userService.update({
      settings: {
        emailFlow,
      },
    });

    // Avoid early trigger & double updating
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluatedEmailFlowAttributes]);

  const updateScreensPath = useCallback(
    (screenId: string) => {
      funnelSettingsService.setScreenId(screenId);
      funnelStepService.updateScreensPath(screenId);
    },
    [funnelSettingsService, funnelStepService],
  );

  const updateUserData = useCallback(
    (dataKey: string, data: UserResponse) => {
      updateUserResponses({ [dataKey]: data });
      updateUserPreferences({ [dataKey]: data.value });
    },
    [updateUserResponses, updateUserPreferences],
  );

  const contextValue: QuizContextData = {
    completeQuiz,
    userPreferences,
    updateUserSettings: updateUserSettingsRequest,
    updateUserEmailConsent: updateEmailConsentRequest,
    getScreenAttributes,
    next: goNext,
    extraNext: goExtraNext,
    back: goBack,
    link,
    signIn: redirectToLogin,
    flowInitialScreenId: flow.initialScreen,
    updateDynamicAttributes,
    updateScreensPath,
    userResponses,
    updateUserData,
    fetchOffers,
    updateFieldInteraction,
    flowContent: evaluatedFlowAttributes,
    emailFlowAttributes: evaluatedEmailFlowAttributes,
  };

  return <QuizContext.Provider value={contextValue}>{children}</QuizContext.Provider>;
};

export const useQuizStep = (id: string) => {
  const {
    back,
    next,
    extraNext,
    getScreenAttributes,
    updateUserSettings,
    updateUserEmailConsent,
    userPreferences,
    completeQuiz,
    link,
    signIn,
    updateDynamicAttributes,
    updateScreensPath,
    flowInitialScreenId,
    userResponses,
    updateUserData,
    fetchOffers,
    updateFieldInteraction,
    flowContent,
    emailFlowAttributes,
  } = useContext(QuizContext);

  const screenAttributes = useMemo(() => getScreenAttributes(id), [id, getScreenAttributes]);
  const screenAnalytics = useDeepMemo(screenAttributes.analytic);

  const initialStep = screenAttributes.index;

  const isTransitScreen = transitScreenTypes.has(screenAttributes.screenType);
  const screenId = id === 'unknown' ? flowInitialScreenId : id;

  useIsomorphicLayoutEffect(() => {
    if (isTransitScreen) return;

    updateScreensPath(screenId);
  }, [updateScreensPath, screenId, isTransitScreen]);

  const analyticsHandlers = useAnalytics(screenAnalytics);

  const isInitialScreen = useMemo(
    () => flowInitialScreenId === screenId,
    [flowInitialScreenId, screenId],
  );

  const autoEnableConsent = useMemo(
    () => screenAttributes.screenType.includes(ScreenType.EMAIL_MARKETING),
    [screenAttributes.screenType],
  );

  const { enableAllConsent, getConsentGeolocation } = useCookieConsent({
    autoEnableConsent,
  });
  const { trackScreenRendered, trackRedirectToNextScreen } = useObservability();

  const [step, setStep] = useState(initialStep);

  usePostMessageToFunnelBuilderOnRouteChange(id);

  useEffect(() => {
    trackScreenRendered(flowInitialScreenId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const backStep = useCallback(() => {
    setStep((prev) => prev - 1);
    back(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const nextScreen = useCallback(
    async ({ value }: { value?: string | string[] } = {}) => {
      const { country } = getConsentGeolocation();

      if (!autoEnableConsent && isInitialScreen && country === Locales.US) {
        enableAllConsent();
      }

      setStep((prev) => prev + 1);
      await next({ id, value });
      trackRedirectToNextScreen(flowInitialScreenId);

      if (screenAttributes.dataKey) {
        updateDynamicAttributes(screenAttributes.dataKey, id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [next, id],
  );

  const extraNextScreen = useCallback(() => extraNext(id), [extraNext, id]);

  return {
    completeQuiz,
    back: backStep,
    next: nextScreen,
    extraNext: extraNextScreen,
    screenAttributes,
    getScreenAttributes,
    updateUserSettings,
    updateUserEmailConsent,
    userPreferences,
    step,
    link,
    signIn,
    analyticsHandlers,
    userResponses,
    updateUserData,
    fetchOffers,
    isInitialScreen,
    updateFieldInteraction,
    flowContent,
    emailFlowAttributes,
  };
};
